import { OperationVariables } from "@apollo/client";
import { CheckIcon } from "lucide-react-native";
import React, { useCallback, useMemo } from "react";
import { Box } from "../../ui/box";
import { Checkbox, CheckboxIcon, CheckboxIndicator } from "../../ui/checkbox";
import { TableData, TableRow } from "../../ui/table";
import { Text } from "../../ui/text";
import { alignToJustify } from "./helpers";
import { DataGridProps } from "./types";

type RowProps<T extends object> = {
  item: T;
  rowIndex: number;
  totalRows: number;
  isSelected: boolean;
};

const RowFunc = <
  T extends object,
  TData,
  TVariables extends OperationVariables & { after?: string },
  F extends object,
  D extends object,
>(
  props: DataGridProps<T, TData, TVariables, F, D> & RowProps<T>,
) => {
  const {
    getId,
    item,
    checkbox,
    size,
    rowIndex,
    columns,
    isSelected,
    onSelectionChange,
    totalRows,
    showFooter,
    rowDisabled,
    renderExpandedRow,
    isRowExpanded,
  } = props;
  const isExpanded = isRowExpanded?.(item) ?? false;
  const itemId = useMemo(() => getId(item), [getId, item]);
  const isLastRow = useMemo(
    () => rowIndex === totalRows - 1,
    [rowIndex, totalRows],
  );
  const onCheckboxChange = useCallback(
    (value: boolean) => {
      onSelectionChange?.((prev) => {
        if (value) {
          return [...prev, itemId];
        }
        return prev.filter((id) => id !== itemId);
      });
    },
    [onSelectionChange, itemId],
  );
  return (
    <>
      <TableRow key={itemId} className="">
        {checkbox && (
          <TableData
            key={"checkbox"}
            size={size}
            isLastRow={!showFooter && isLastRow}
            isSelected={isSelected}
          >
            <Checkbox
              size="sm"
              value={itemId}
              isChecked={isSelected}
              action="brand"
              onChange={onCheckboxChange}
              isDisabled={rowDisabled?.(item) || false}
            >
              <CheckboxIndicator>
                <CheckboxIcon as={CheckIcon} />
              </CheckboxIndicator>
            </Checkbox>
          </TableData>
        )}
        {columns.map((column) => {
          const content = column.render?.(item, rowIndex) || (
            <Box>
              <Text size="sm" action="secondary" numberOfLines={1} isTruncated>
                {column.getValue?.(item)}
              </Text>
            </Box>
          );
          return (
            <TableData
              key={String(column.id)}
              size={size}
              isLastRow={!showFooter && isLastRow}
              isSelected={isSelected}
            >
              {column.align && column.align !== "left" ? (
                <Box
                  className={`flex-row w-full ${alignToJustify(column.align)}`}
                >
                  {content}
                </Box>
              ) : (
                content
              )}
            </TableData>
          );
        })}
      </TableRow>
      {/* Mounted only while open, not rendered-then-hidden. A collapsed row
          that still ran `renderExpandedRow` would run whatever is inside it
          for every row of the table — so a detail panel that loads its own
          data could not exist without firing one request per row on first
          paint. Unmounting on collapse is also what lets the panel's own
          cache serve an instant re-open. */}
      {renderExpandedRow && isExpanded && (
        <TableRow aria-expanded className="table-row">
          <TableData
            colspan={columns.length + (checkbox ? 1 : 0)}
            size={size}
            isLastRow={!showFooter && isLastRow}
            className={`p-0`}
          >
            {renderExpandedRow(item)}
          </TableData>
        </TableRow>
      )}
    </>
  );
};

const Row = React.memo(RowFunc) as typeof RowFunc;

export default Row;
